import { can } from '@clim/client-core';
import type { PermissionDef, Scope } from '@clim/domain';
import { useState } from 'react';
import { ErrorBox, Field, Input, Modal } from '../../components/ui';
import { useReady } from '../../lib/session';
import { useApi, useMutation } from './common';

interface RoleRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount: number;
  grants: { permission: string; scope: Scope }[];
}

const SCOPE_LABEL: Record<Scope | '', string> = { '': '—', own: 'Les siens', territory: 'Son territoire', all: 'Tout' };

export function RolesAdmin() {
  const { me } = useReady();
  const roles = useApi<RoleRow[]>('/v1/admin/roles');
  const perms = useApi<PermissionDef[]>('/v1/admin/permissions');
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const role = roles.data?.find((r) => r.id === selected) ?? roles.data?.[0];
  const editable = !!role && !role.isSystem && can(me, 'role.manage');
  const m = useMutation();
  const [draft, setDraft] = useState<Record<string, Scope | ''> | null>(null);
  const grants: Record<string, Scope | ''> = draft ?? Object.fromEntries(role?.grants.map((g) => [g.permission, g.scope]) ?? []);
  const modules = [...new Set(perms.data?.map((p) => p.module))];

  const save = async () => {
    if (!role || !draft) return;
    const list = Object.entries(draft)
      .filter(([, s]) => s)
      .map(([permission, scope]) => ({ permission, scope }));
    await m.run('PUT', `/v1/admin/roles/${role.id}/permissions`, list);
    setDraft(null);
    roles.reload();
  };

  return (
    <div>
      <div className="chips">
        {roles.data?.map((r) => (
          <button
            key={r.id}
            className={`chip${role?.id === r.id ? ' on' : ''}`}
            onClick={() => {
              setSelected(r.id);
              setDraft(null);
            }}
          >
            {r.name} ({r.userCount})
          </button>
        ))}
        {can(me, 'role.manage') && (
          <button className="chip" onClick={() => setCreating(true)}>
            + Rôle
          </button>
        )}
      </div>
      {role?.isSystem && <p className="muted">Rôle système : toutes les permissions, non modifiable.</p>}
      <ErrorBox message={roles.error ?? perms.error ?? m.error} />
      {modules.map((mod) => (
        <div key={mod} className="card">
          <h3>{mod}</h3>
          <table className="table compact">
            <tbody>
              {perms.data
                ?.filter((p) => p.module === mod)
                .map((p) => (
                  <tr key={p.code} className={p.status === 'planned' ? 'planned' : ''}>
                    <td>
                      {p.label}
                      {p.status === 'planned' && <span className="muted"> (module à venir)</span>}
                    </td>
                    <td className="nowrap">
                      <select
                        className="input"
                        disabled={!editable}
                        value={grants[p.code] ?? ''}
                        onChange={(e) => setDraft({ ...grants, [p.code]: e.target.value as Scope | '' })}
                        aria-label={p.label}
                      >
                        {(p.scoped ? (['', 'own', 'territory', 'all'] as const) : (['', 'all'] as const)).map((s) => (
                          <option key={s} value={s}>
                            {SCOPE_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ))}
      {draft && (
        <div className="sticky-actions">
          <button className="btn" onClick={() => setDraft(null)}>
            Annuler
          </button>
          <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
            Enregistrer les permissions
          </button>
        </div>
      )}
      {creating && <NewRole onClose={() => setCreating(false)} onSaved={() => { setCreating(false); roles.reload(); }} />}
    </div>
  );
}

function NewRole({ onClose, onSaved }: { onClose(): void; onSaved(): void }) {
  const m = useMutation();
  const [f, setF] = useState({ code: '', name: '', description: '' });
  return (
    <Modal title="Nouveau rôle" onClose={onClose}>
      <div className="form">
        <Field label="Nom">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, code: e.target.value.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })} />
        </Field>
        <Field label="Code technique">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
        <Field label="Description">
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" onClick={() => void m.run('POST', '/v1/admin/roles', f).then(onSaved, () => undefined)}>
            Créer
          </button>
        </div>
      </div>
    </Modal>
  );
}
